import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, useMediaQuery, useTheme, Typography, Menu, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Switch, TextField, Snackbar, CircularProgress } from '@mui/material';
import { format, parse } from 'date-fns';
import ActionButton from '../../components/ActionButton';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import CalendarSidebar from './CalendarSidebar';
import CalendarMain from './CalendarMain';
import CalendarSubSidebar from './CalendarSubSidebar';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';

const Calendar = () => {
  console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const userId = localStorage.getItem('userId');
  const [entities, setEntities] = useState([]);
  const [openAddMeetingDialog, setOpenAddMeetingDialog] = useState(false);
const [newMeeting, setNewMeeting] = useState({
    meetingWith: '',
    startTime: null,
    endTime: null,
    meetMode: '',
    meetMethod: '',
    phone: ''
  });
const [formSubmitted, setFormSubmitted] = useState(false);
  const [globalAvailabilityForDate, setGlobalAvailabilityForDate] = useState(null);

  const fetchGlobalAvailabilityForDate = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/calendar/get-availability`, {
        params: { 
          date: format(selectedDate, 'yyyy-MM-dd'),
          userId: userId
        }
      });
      if (response.data && response.data.length > 0) {
        const weekday = format(selectedDate, 'EEEE').toLowerCase();
        const dayAvailability = response.data.find(day => day.weekday.toLowerCase() === weekday);
        setGlobalAvailabilityForDate(dayAvailability);
      } else {
        setGlobalAvailabilityForDate(null);
      }
    } catch (error) {
      console.error('Error fetching global availability:', error);
      setGlobalAvailabilityForDate(null);
    }
  }, [selectedDate, userId]);

  useEffect(() => {
    fetchGlobalAvailabilityForDate();
  }, [fetchGlobalAvailabilityForDate, selectedDate]);

  const handleAddMeeting = (entity, date) => {
    setSelectedEntity(entity);
    setSelectedDate(date);
    setOpenAddMeetingDialog(true);
  };

  const handleCloseAddMeetingDialog = () => {
    setOpenAddMeetingDialog(false);
    setNewMeeting({
      meetingWith: '',
      startTime: null,
      endTime: null,
      meetMode: '',
      meetMethod: '',
      phone: ''
    });
  };

const handleSaveNewMeeting = async () => {
  setFormSubmitted(true);

  // Check if all required fields are filled
  const requiredFields = ['meetingWith', 'startTime', 'endTime', 'phone'];
  const missingFields = requiredFields.filter(field => !newMeeting[field]);

  if (missingFields.length > 0 || !selectedEntity || !selectedEntity.entityNumber) {
    console.log('Missing fields:', missingFields);
    console.log('Selected entity:', selectedEntity);
    setConflictErrorMessage("Please fill in all required fields.");
    setConflictDialogOpen(true);
    return;
  }

  try {
    const formatTime = (time) => {
      if (time instanceof Date) {
        return format(time, 'HH:mm');
      }
      return time;
    };

    const meetingData = {
      userId,
      uniqueId: selectedEntity.uniqueId?.S || selectedEntity.uniqueId,
      entityName: selectedEntity.entityName,
      entityNumber: selectedEntity.entityNumber?.S || selectedEntity.entityNumber,
      weekday: format(selectedDate, 'EEEE').toLowerCase(),
      meetingDate: format(selectedDate, 'yyyy-MM-dd'),
      meetingWith: newMeeting.meetingWith.trim(),
      startTime: formatTime(newMeeting.startTime),
      endTime: formatTime(newMeeting.endTime),
      meetMode: newMeeting.meetMode.trim(),
      meetMethod: newMeeting.meetMethod.trim(),
      phone: newMeeting.phone.trim()
    };

    // Validate meeting data
    const requiredDataFields = ['userId', 'uniqueId', 'entityName', 'entityNumber', 'weekday', 'meetingDate', 'meetingWith', 'startTime', 'endTime', 'phone'];
    const missingDataFields = requiredDataFields.filter(field => !meetingData[field]);
    if (missingDataFields.length > 0) {
      console.log('Missing data fields:', missingDataFields);
      throw new Error(`Missing required fields: ${missingDataFields.join(', ')}`);
    }

    console.log('Sending meeting data:', JSON.stringify(meetingData, null, 2));
    console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);

    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/meetings`, meetingData);
    console.log('New meeting saved:', response.data);

    setOpenAddMeetingDialog(false);
    setRefreshTrigger(prev => prev + 1);
    setSuccessMessage('Meeting added successfully');
    
    // Reset the form
    setNewMeeting({
      meetingWith: '',
      startTime: null,
      endTime: null,
      meetMode: '',
      meetMethod: '',
      phone: ''
    });
    setFormSubmitted(false);
  } catch (error) {
    console.error('Error saving new meeting:', error);
    console.error('Error details:', error.response ? error.response.data : error.message);

    let errorMessage = 'An error occurred while adding the meeting.';

    if (error.response) {
      switch (error.response.status) {
        case 400:
          errorMessage = 'Invalid meeting data. Please check all fields and try again.';
          break;
        case 401:
          errorMessage = 'Unauthorized. Please log in and try again.';
          break;
        case 403:
          errorMessage = 'You do not have permission to add meetings for this entity.';
          break;
        case 409:
          errorMessage = error.response.data.error || 'A meeting with conflicting time already exists for this user, entity, and date.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later or contact support.';
          break;
        default:
          errorMessage = error.response.data.error || 'An unexpected error occurred. Please try again.';
      }
    } else if (error.request) {
      errorMessage = 'No response received from the server. Please check your internet connection and try again.';
    } else if (error.message.includes('No time slots found')) {
      errorMessage = 'No available time slots found for this day. Please check the entity\'s availability or choose a different day.';
    } else {
      errorMessage = `Failed to add new meeting: ${error.message}`;
    }

    setConflictErrorMessage(errorMessage);
    setConflictDialogOpen(true);
  }
};

  const handleCloseConflictDialog = () => {
    setConflictDialogOpen(false);
    setConflictErrorMessage('');
  };

  const handleChangeSlot = () => {
    setNewMeeting(prevMeeting => ({
      ...prevMeeting,
      startTime: null,
      endTime: null
    }));
    setConflictDialogOpen(false);
    setOpenAddMeetingDialog(true); // Reopen the add meeting dialog
  };

const renderConflictDialog = () => (
  <Dialog open={conflictDialogOpen} onClose={handleCloseConflictDialog}>
    <DialogTitle>Error</DialogTitle>
    <DialogContent>
      <Typography>{conflictErrorMessage}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCloseConflictDialog}>Close</Button>
      {conflictErrorMessage.includes('conflicting time') && (
        <Button onClick={handleChangeSlot} variant="contained">Change Slot</Button>
      )}
    </DialogActions>
  </Dialog>
);

const renderAddMeetingDialog = () => {
    const isFormValid = newMeeting.meetingWith && newMeeting.startTime && newMeeting.endTime && newMeeting.phone;

    return (
      <Dialog open={openAddMeetingDialog} onClose={handleCloseAddMeetingDialog}>
        <DialogTitle>Add New Meeting</DialogTitle>
        <DialogContent>
          {newMeeting.error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {newMeeting.error}
            </Typography>
          )}
          {conflictErrorMessage && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {conflictErrorMessage}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Meeting With"
            fullWidth
            required
            value={newMeeting.meetingWith}
            onChange={(e) => setNewMeeting({ ...newMeeting, meetingWith: e.target.value })}
            error={formSubmitted && !newMeeting.meetingWith}
            helperText={formSubmitted && !newMeeting.meetingWith ? "This field is required" : ""}
          />
          <TimePicker
            label="Start Time"
            value={newMeeting.startTime}
            onChange={(newValue) => setNewMeeting({ ...newMeeting, startTime: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth margin="dense" required error={formSubmitted && !newMeeting.startTime} helperText={formSubmitted && !newMeeting.startTime ? "This field is required" : ""} />}
          />
          <TimePicker
            label="End Time"
            value={newMeeting.endTime}
            onChange={(newValue) => setNewMeeting({ ...newMeeting, endTime: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth margin="dense" required error={formSubmitted && !newMeeting.endTime} helperText={formSubmitted && !newMeeting.endTime ? "This field is required" : ""} />}
          />
          <TextField
            margin="dense"
            label="Meet Mode"
            fullWidth
            value={newMeeting.meetMode}
            onChange={(e) => setNewMeeting({ ...newMeeting, meetMode: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Meet Method"
            fullWidth
            value={newMeeting.meetMethod}
            onChange={(e) => setNewMeeting({ ...newMeeting, meetMethod: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            required
            value={newMeeting.phone}
            onChange={(e) => setNewMeeting({ ...newMeeting, phone: e.target.value })}
            error={formSubmitted && !newMeeting.phone}
            helperText={formSubmitted && !newMeeting.phone ? "This field is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMeetingDialog}>Cancel</Button>
          <ActionButton text="Save" onClick={handleSaveNewMeeting} disabled={!isFormValid} />
        </DialogActions>
      </Dialog>
    );
  };
  
  const handleEntitySelect = useCallback((entity) => {
    setSelectedEntity(entity);
  }, []);

const fetchEntityData = useCallback(async (entity, date) => {
  console.log('Fetching entity data for:', entity);
  if (entity && entity.uniqueId) {
    try {
      const entityId = entity.uniqueId?.S || entity.uniqueId;
      const entityName = entity.entityName?.S || entity.entityName;
      const entityNumber = entity.entityNumber?.S || entity.entityNumber;

      console.log('Entity details:', { entityId, entityName, entityNumber });

      if (!entityName || !entityNumber) {
        console.warn('Entity name or number is missing:', { entityName, entityNumber });
        setEntityData(null);
        return;
      }

      const formattedDate = format(date, 'yyyy-MM-dd');
      console.log('Fetching data for date:', formattedDate);

      const [availabilityResponse, meetingsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/calendar/get-availability`, {
          params: { userId, entityId, meetingDate: formattedDate },
          headers: { 'Content-Type': 'application/json' }
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/meetings`, {
          params: { entityName, entityNumber, date: formattedDate },
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      console.log('Availability response:', availabilityResponse.data);
      console.log('Meetings response:', meetingsResponse.data);

      setEntityData({
        availability: availabilityResponse.data,
        meetings: meetingsResponse.data.meetings
      });
    } catch (error) {
      console.error('Error fetching entity data:', error);
      setError(error.message || 'Failed to fetch entity data');
      setEntityData(null);
    }
  } else {
    console.log('No entity or entity.uniqueId provided');
    setEntityData(null);
  }
}, [userId]);

  useEffect(() => {
    if (selectedEntity) {
      fetchEntityData(selectedEntity, selectedDate);
    }
  }, [selectedEntity, selectedDate, fetchEntityData]);

  const [entityData, setEntityData] = useState(null);

  const memoizedEntityData = useMemo(() => {
    if (selectedEntity && entityData) {
      return {
        ...selectedEntity,
        ...entityData
      };
    }
    return selectedEntity;
  }, [selectedEntity, entityData]);

  const selectedEntityRef = useRef(selectedEntity);
  useEffect(() => {
    selectedEntityRef.current = selectedEntity;
  }, [selectedEntity]);

  const fetchMeetings = useCallback(async () => {
    if (selectedEntity) {
      try {
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/meetings`, {
          params: { 
            entityName: selectedEntity.entityName,
            entityNumber: selectedEntity.entityNumber?.S || selectedEntity.entityNumber,
            date: formattedDate 
          },
          headers: { 'Content-Type': 'application/json' }
        });
        setSelectedEntity(prevEntity => ({
          ...prevEntity,
          meetings: response.data.meetings
        }));
      } catch (error) {
        console.error('Error fetching meetings:', error);
        setError('Failed to fetch meetings');
      }
    }
  }, [selectedEntity, selectedDate]);

  useEffect(() => {
    fetchMeetings();
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      // Log the structure of the availability data
      if (Array.isArray(selectedEntity.availability)) {
        selectedEntity.availability.forEach((item, index) => {
        });
      } else if (typeof selectedEntity.availability === 'object') {
        Object.entries(selectedEntity.availability).forEach(([key, value]) => {
        });
      } else {
        console.log('Availability has unexpected type:', typeof selectedEntity.availability);
      }
    }
  }, [selectedEntity]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [fetchedAvailability, setFetchedAvailability] = useState(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictErrorMessage, setConflictErrorMessage] = useState('');

  const fetchEntities = useCallback(async () => {
    try {
      if (!userId) {
        throw new Error('User ID is not available');
      }
      setLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/entities`;
      const response = await axios.get(url, {
        params: { userId },
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }
      let entitiesData = [];
      if (Array.isArray(response.data)) {
        entitiesData = response.data;
      } else if (response.data && typeof response.data === 'object' && response.data.entities) {
        entitiesData = response.data.entities;
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Unexpected response format');
      }
      // Fetch availability for each entity
      const entitiesWithAvailability = await Promise.all(entitiesData.map(async (entity) => {
        try {
          const availabilityResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/calendar/get-availability`, {
            params: { userId, entityId: entity.uniqueId, meetingDate: format(selectedDate, 'yyyy-MM-dd') },
            headers: { 'Content-Type': 'application/json' }
          });
          return { ...entity, availability: availabilityResponse.data };
        } catch (availabilityError) {
          console.error(`Error fetching availability for entity ${entity.uniqueId}:`, availabilityError);
          return { ...entity, availability: [] };
        }
      }));
      
      
      setEntities(entitiesWithAvailability);
      
      // Set the selected entity after fetching entities
      if (entitiesWithAvailability.length > 0 && !selectedEntity) {
        setSelectedEntity(entitiesWithAvailability[0]);
      }
      
      setError('');
    } catch (error) {
      console.error('Error fetching entities:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      console.error('Error stack:', error.stack);
      setError(error.response?.data?.message || error.message || 'Failed to fetch entities');
      setEntities([]); // Ensure entities is always an array
    } finally {
      setLoading(false);
    }
  }, [userId, selectedEntity]);

  const fetchEntitiesRef = useRef(fetchEntities);
  useEffect(() => {
    fetchEntitiesRef.current = fetchEntities;
  }, [fetchEntities]);

  useEffect(() => {
    fetchEntitiesRef.current();
  }, []);

  const updateEntities = useCallback(() => {
    fetchEntities();
  }, [fetchEntities]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [globalAvailability, setGlobalAvailability] = useState({
    monday: { available: true, startTime: '09:00', endTime: '17:00' },
    tuesday: { available: true, startTime: '09:00', endTime: '17:00' },
    wednesday: { available: true, startTime: '09:00', endTime: '17:00' },
    thursday: { available: true, startTime: '09:00', endTime: '17:00' },
    friday: { available: true, startTime: '09:00', endTime: '17:00' },
    saturday: { available: false, startTime: '09:00', endTime: '17:00' },
    sunday: { available: false, startTime: '09:00', endTime: '17:00' },
  });

  useEffect(() => {
  }, [globalAvailability]);

  const [openAvailabilityDialog, setOpenAvailabilityDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDateChange = (amount) => {
    const newDate = new Date(selectedDate.setMonth(selectedDate.getMonth() + amount));
    setSelectedDate(newDate);
    fetchGlobalAvailabilityForDate();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

const fetchAvailability = useCallback(async () => {
  try {
    if (!userId) {
      throw new Error('User ID is not available');
    }
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-availability`, {
      params: { userId },
      headers: { 'Content-Type': 'application/json' }
    });
    setFetchedAvailability(response.data);
    setError('');
  } catch (error) {
    console.error('Error fetching availability:', error);
    setError(error.response?.data?.message || error.message || 'Failed to fetch availability');
  }
}, [userId]);

  const handleOpenAvailabilityDialog = async () => {
    await fetchAvailability();
    setOpenAvailabilityDialog(true);
  };

  const [editableAvailability, setEditableAvailability] = useState({});

  useEffect(() => {
    if (fetchedAvailability) {
      if (Array.isArray(fetchedAvailability)) {
        const newEditableAvailability = fetchedAvailability.reduce((acc, item) => {
          acc[item.weekday] = { available: true, startTime: item.startTime, endTime: item.endTime };
          return acc;
        }, {});
        setEditableAvailability(newEditableAvailability);
      } else {
        console.error('fetchedAvailability is not an array:', fetchedAvailability);
        // Set default values when fetchedAvailability is not an array
        const defaultAvailability = {
          monday: { available: true, startTime: '09:00', endTime: '17:00' },
          tuesday: { available: true, startTime: '09:00', endTime: '17:00' },
          wednesday: { available: true, startTime: '09:00', endTime: '17:00' },
          thursday: { available: true, startTime: '09:00', endTime: '17:00' },
          friday: { available: true, startTime: '09:00', endTime: '17:00' },
          saturday: { available: false, startTime: '09:00', endTime: '17:00' },
          sunday: { available: false, startTime: '09:00', endTime: '17:00' },
        };
        setEditableAvailability(defaultAvailability);
      }
    }
  }, [fetchedAvailability]);

  const handleGlobalAvailabilityChange = (day, field, value) => {
    setEditableAvailability(prev => {
      const updatedDay = { ...prev[day] } || { available: false };
      if (field === 'available') {
        updatedDay.available = value;
        if (value) {
          updatedDay.startTime = updatedDay.startTime || '09:00';
          updatedDay.endTime = updatedDay.endTime || '17:00';
        }
      } else {
        updatedDay[field] = value;
      }
      const newAvailability = { ...prev, [day]: updatedDay };
      return newAvailability;
    });
  };
  const handleSaveAvailabilityDialog = async () => {
    try {
      if (!userId) {
        console.error('User ID is not available');
        setError('Failed to get userID');
        return;
      }
      setSavingAvailability(true);
      
      if (!userId) {
        setError('User ID is not available. Please try again or contact support.');
        return;
      }

      const availabilityToSave = Object.entries(editableAvailability).reduce((acc, [weekday, { available, startTime, endTime }]) => {
        if (available && startTime && endTime) {
          const formattedStartTime = format(new Date(`1970-01-01T${startTime}`), 'HH:mm');
          const formattedEndTime = format(new Date(`1970-01-01T${endTime}`), 'HH:mm');
          if (formattedStartTime >= formattedEndTime) {
            throw new Error(`Invalid time range for ${weekday}: start time must be before end time`);
          }

          acc[weekday.toLowerCase()] = {
            available: true,
            startTime: formattedStartTime,
            endTime: formattedEndTime
          };
        }
        return acc;
      }, {});
      if (Object.keys(availabilityToSave).length === 0) {
        setError('No availability set. Please set availability for at least one day.');
        return;
      }

      const payload = {
        userId: userId,
        availability: availabilityToSave
      };

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/global-availability`, payload);
      
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response from server');
      }
      if (response.data.message === "Global availability settings saved successfully.") {
        // Check if the server returned the saved availability
        let savedAvailability;
        if (response.data.allUserAvailability && Array.isArray(response.data.allUserAvailability) && response.data.allUserAvailability.length > 0) {
          savedAvailability = response.data.allUserAvailability.reduce((acc, item) => {
            acc[item.weekday] = { available: true, startTime: item.startTime, endTime: item.endTime };
            return acc;
          }, {});
        } else {
          console.warn('Server did not return saved availability data. Using local data.');
          savedAvailability = availabilityToSave;
        }
        setEditableAvailability(savedAvailability);
        setGlobalAvailability(prevGlobal => ({
          ...prevGlobal,
          ...Object.entries(savedAvailability).reduce((acc, [day, { startTime, endTime }]) => {
            acc[day] = {
              ...prevGlobal[day],
              available: true,
              startTime: new Date(`1970-01-01T${startTime}`),
              endTime: new Date(`1970-01-01T${endTime}`)
            };
            return acc;
          }, {})
        }));
        setOpenAvailabilityDialog(false);
        setSuccessMessage('Global availability saved successfully');
        setTimeout(() => setSuccessMessage(''), 5000); // Clear the message after 5 seconds
        setRefreshTrigger(prev => prev + 1); // Trigger refresh of CalendarSubSidebar
        setRefreshTrigger(prev => prev + 1);
      } else {
        console.error('Unexpected server response:', response.data);
        throw new Error('Unexpected server response');
      }
    } catch (error) {
      console.error('Error saving global availability:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      setError(`Error saving global availability: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleCancelAvailabilityDialog = () => {
    setOpenAvailabilityDialog(false);
  };

  const handleCloseError = () => {
    setError('');
  };

  const renderAvailabilityDialog = () => {
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    return (
      <Dialog open={openAvailabilityDialog} onClose={handleCancelAvailabilityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Global Availability</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {weekdays.map((day) => {
              const item = editableAvailability[day] || { available: false, startTime: '09:00', endTime: '17:00' };
              const available = item.available;

              return (
                <Grid item xs={12} key={day}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ width: '100px' }}>{day.charAt(0).toUpperCase() + day.slice(1)}</Typography>
                    <Switch
                      checked={available}
                      onChange={() => handleGlobalAvailabilityChange(day, 'available', !available)}
                      inputProps={{ 'aria-label': `${day} availability toggle` }}
                    />
                    {available && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexGrow: 1 }}>
                        <TimePicker
                          value={new Date(`1970-01-01T${item.startTime}`)}
                          onChange={(newValue) => handleGlobalAvailabilityChange(day, 'startTime', format(newValue, 'HH:mm'))}
                          renderInput={(params) => <TextField {...params} size="small" sx={{ width: 110 }} />}
                        />
                        <Typography sx={{ mx: 1 }}>-</Typography>
                        <TimePicker
                          value={new Date(`1970-01-01T${item.endTime}`)}
                          onChange={(newValue) => handleGlobalAvailabilityChange(day, 'endTime', format(newValue, 'HH:mm'))}
                          renderInput={(params) => <TextField {...params} size="small" sx={{ width: 110 }} />}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAvailabilityDialog} disabled={savingAvailability}>Close</Button>
          <Button onClick={handleSaveAvailabilityDialog} variant="contained" disabled={savingAvailability}>
            {savingAvailability ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box
            sx={{
              backgroundColor: 'error.main',
              color: 'error.contrastText',
              padding: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              boxShadow: 3,
              minWidth: '300px',
            }}
          >
            <Typography variant="body1">{error}</Typography>
          </Box>
        </Snackbar>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={5000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box
            sx={{
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              padding: 2,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              boxShadow: 3,
              minWidth: '300px',
            }}
          >
            <Typography variant="body1">{successMessage}</Typography>
          </Box>
        </Snackbar>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          isMobile ? (
          <>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
<Box sx={{
  backgroundColor: '#f0f8ff',
  padding: '8px',
  borderRadius: '4px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  marginBottom: '16px'
}}>
  {globalAvailabilityForDate ? (
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
      Global availability: {format(parse(globalAvailabilityForDate.startTime, 'HH:mm', new Date()), 'h:mm a')} - 
      {format(parse(globalAvailabilityForDate.endTime, 'HH:mm', new Date()), 'h:mm a')}
    </Typography>
  ) : (
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
      Global availability: Not available
    </Typography>
  )}
</Box>
                <ActionButton
                  text="Set Global Availability"
                  onClick={handleOpenAvailabilityDialog}
                  startIcon={<SettingsIcon />}
                />
              </Box>
              <Box 
                sx={{ 
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onClick={handleMenuOpen}
              >
                <Box>
                  <Typography variant="subtitle1">
                    {selectedEntity ? selectedEntity.name : 'Select an entity'}
                  </Typography>
                  <Typography variant="caption">{format(selectedDate, 'MMMM d, yyyy')}</Typography>
                </Box>
                <ArrowDropDownIcon />
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <CalendarMain
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  handleDateChange={handleDateChange}
                  isMobile={isMobile}
                  globalAvailability={globalAvailability}
                />
              </Box>
              <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
            <CalendarSubSidebar
              selectedEntity={selectedEntity}
              selectedDate={selectedDate}
            />
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  width: '100%',
                  maxWidth: 'none',
                  left: 0,
                  right: 0,
                },
              }}
            >
              <CalendarSidebar
                entities={entities}
                selectedEntity={selectedEntity}
                setSelectedEntity={setSelectedEntity}
                isMobile={isMobile}
                closeMenu={handleMenuClose}
                globalAvailability={globalAvailability}
                updateEntities={updateEntities}
                loading={loading}
                error={error}
              />
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            <CalendarSidebar
              entities={entities}
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
              isMobile={isMobile}
              globalAvailability={globalAvailability}
              updateEntities={updateEntities}
              loading={loading}
              error={error}
              handleEntitySelect={handleEntitySelect}
            />
            {console.log('Calendar - Before passing to CalendarSubSidebar:', { 
              selectedEntity: selectedEntity ? { 
                ...selectedEntity, 
                availability: Array.isArray(selectedEntity.availability) 
                  ? selectedEntity.availability 
                  : Object.entries(selectedEntity.availability || {}).map(([weekday, data]) => ({ weekday, ...data }))
              } : null,
              selectedDate,
              globalAvailability
            })}
            <CalendarSubSidebar
              key={memoizedEntityData ? memoizedEntityData.uniqueId : 'no-entity'}
              selectedEntity={memoizedEntityData}
              selectedDate={selectedDate}
              globalAvailability={globalAvailability}
              refreshTrigger={refreshTrigger}
              onAddMeeting={handleAddMeeting}
              ActionButton={ActionButton}
            />
            {console.log('Calendar - After rendering CalendarSubSidebar')}
            {selectedEntity && console.log('Calendar - Selected entity availability:', 
              Array.isArray(selectedEntity.availability) 
                ? selectedEntity.availability 
                : Object.entries(selectedEntity.availability || {}).map(([weekday, data]) => ({ weekday, ...data }))
            )}
            <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2 }}>
                {/* Commented out Global Availability display
                <Box>
                  {globalAvailabilityForDate ? (
                    <Typography variant="" sx={{ color: '#0d47a1' }}>
                      Global Availability: {format(parse(globalAvailabilityForDate.startTime, 'HH:mm', new Date()), 'h:mm a')} - 
                      {format(parse(globalAvailabilityForDate.endTime, 'HH:mm', new Date()), 'h:mm a')}
                    </Typography>
                  ) : (
                    <Typography variant="" sx={{ color: '#0d47a1' }}>Global Availability: Didn't Set For This Day</Typography>
                  )}
                </Box>
                */}
                <Box>
                  {/* Commented out Set Global Availability button
                  <ActionButton
                    text="Set Global Availability"
                    onClick={handleOpenAvailabilityDialog}
                    startIcon={<SettingsIcon />}
                    sx={{ mr: 2 }}
                  />
                  */}
                  <ActionButton
                    text="Add Meeting"
                    onClick={() => handleAddMeeting(selectedEntity, selectedDate)}
                    startIcon={<AddIcon />}
                  />
                </Box>
              </Box>
              <CalendarMain
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleDateChange={handleDateChange}
                isMobile={isMobile}
                globalAvailability={globalAvailability}
              />
            </Box>
          </Box>
          )
        )}
        {renderAvailabilityDialog()}
        {renderAddMeetingDialog()}
        {renderConflictDialog()}
      </Box>
    </LocalizationProvider>
  );
};

export default Calendar;
